import React, { useCallback, useRef, useState } from 'react';
import { InteractionManager, Platform, StatusBar as RNStatusbar, View, } from 'react-native';
import { interpolateColor, useAnimatedStyle, useSharedValue, } from 'react-native-reanimated';
import { NavigationHeader, rem } from 'design-system';
import FlatListItemCard from 'design-system/FlatListItemCard';
import SearchBar from 'design-system/SearchBar';
import StatusBar from 'design-system/StatusBar';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { KeyboardAwareFlatList } from 'utils/packages/rnKeyboardAwareScrollview';
import { isAndroid, isIOS, isWeb } from '../../constants';
import PhonePrefixesRow from './PhonePrefixesRow';
import prefixes from './prefixes';
const UK_ALT = [
    'england',
    'scotland',
    'wales',
    'ni',
    'gb',
    'uk',
    'great',
    'britain',
];
const keyExtractor = (item) => prefixes[item].iso2;
const PhonePrefixes = ({ navigation, route, }) => {
    const search = useRef(null);
    const [filteredCountries, setFilteredCountries] = useState(Object.keys(prefixes).sort());
    const styles = useStyles(styleSet);
    useAutoFocusInput(search);
    const onChangeText = (data) => {
        const textWord = data.replace(/[^a-z0-9]|\s+|\r?\n|\r]/gi, ' ');
        const results = Object.keys(prefixes).filter((country) => {
            const wordsToMatch = textWord.toLowerCase().split(' ');
            const countryLower = country.toLowerCase();
            const iso = prefixes[country].iso2.toLowerCase();
            const countryPrefix = prefixes[country].prefix.substr(1);
            if (wordsToMatch.some((word) => UK_ALT.includes(word)) &&
                country === 'United Kingdom') {
                return true;
            }
            return wordsToMatch.every((word) => countryLower.match(word.toLowerCase()) ||
                iso.match(word.toLowerCase()) ||
                countryPrefix.match(word.toLowerCase()));
        });
        setFilteredCountries(results);
    };
    const { onPickCancel, onSelectedPrefix } = route.params;
    const onSearchCancel = useCallback(() => {
        onPickCancel();
        navigation.goBack();
    }, [onPickCancel]);
    const onPress = useCallback((data) => {
        onPickCancel();
        onSelectedPrefix(data);
        InteractionManager.runAfterInteractions(() => {
            navigation.goBack();
        });
    }, [onPickCancel, onSelectedPrefix]);
    const renderItem = useCallback(({ item, index }) => (<FlatListItemCard index={index} style={styles.card} onPress={() => onPress({ item: prefixes[item], country: item })} itemsLength={filteredCountries.length}>
        <PhonePrefixesRow country={item} item={prefixes[item]}/>
      </FlatListItemCard>), [filteredCountries.length, onPress, styles.card]);
    const scrollY = useSharedValue(0);
    const onScroll = useCallback((event) => {
        scrollY.value = event.nativeEvent.contentOffset.y;
    }, [scrollY]);
    const colors = useColors();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, Platform.OS === 'ios' ? undefined : paddingHorizontalStyle, styles.contentContainerStyles);
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors]);
    return (<View style={styles.container}>
      {isIOS ? (<RNStatusbar barStyle="light-content" animated/>) : (<StatusBar barStyle="dark-content" animated/>)}
      <View style={[styles.content]}>
        <NavigationHeader scrollY={scrollY} noPaddingTop isInTab={isWeb} skipSafeArea={isIOS} withFrame={Platform.OS !== 'ios'}>
          <SearchBar ref={search} onCancel={onSearchCancel} forSearchHeader={isAndroid} onChangeSearchTerm={onChangeText} textInputBgStyles={textInputStyle}/>
        </NavigationHeader>
        <View style={styles.content}>
          <KeyboardAwareFlatList onScroll={onScroll} scrollEventThrottle={16} contentContainerStyle={paddingBottom} keyboardShouldPersistTaps="always" renderItem={renderItem} keyExtractor={keyExtractor} data={filteredCountries}/>
        </View>
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
    },
    content: {
        flex: 1,
        backgroundColor: colors.background.dark,
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    contentContainerStyles: {
        paddingTop: rem(16),
    },
}));
export default PhonePrefixes;
